/**
 * Vendor card — grid-view variant of `<VendorRow>`.
 *
 * Same data, tile shape. Mirrors `features/customers/components/customer-card.tsx`.
 *
 * @see prototype/purchases/vendors.html grid-view button — `.view-toggle [aria-label="Grid view"]`
 */
import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { vendors as contract } from "@flint/api-contract";
import type { z } from "zod";
import { StatusPill } from "@flint/ui";
import { VendorAvatar } from "./vendor-avatar.tsx";

type ListRow = z.infer<typeof contract.listRow>;

export interface VendorCardProps {
  vendor: ListRow;
}

export function VendorCard({ vendor }: VendorCardProps) {
  const isArchived = vendor.archivedAt !== null;

  return (
    <Link
      to="/procurement/vendors/$vendorId"
      params={{ vendorId: vendor.id }}
      className="group flex flex-col gap-3 p-4 bg-paper border border-line rounded-md hover:border-line-2 hover:bg-raised transition-colors"
    >
      <div className="flex items-start justify-between">
        <VendorAvatar name={vendor.name} size="lg" />
        <ChevronRight
          size={14}
          strokeWidth={2}
          aria-hidden
          className="text-muted group-hover:text-ink-2 transition-colors mt-1"
        />
      </div>

      <div className="min-w-0">
        <p className="font-medium text-ink truncate">{vendor.name}</p>
        <p className="text-xs text-muted mt-0.5 truncate">
          {vendor.gstin ? (
            <span className="font-mono">{vendor.gstin}</span>
          ) : (
            <span className="italic">No GSTIN</span>
          )}
        </p>
        {vendor.city || vendor.contactName ? (
          <p className="text-xs text-muted truncate">
            {[vendor.city, vendor.contactName].filter(Boolean).join(" · ")}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {isArchived ? (
          <StatusPill tone="muted">Archived</StatusPill>
        ) : (
          <StatusPill tone="muted" title="Health derives from bills — wires up next">
            No data yet
          </StatusPill>
        )}
        {vendor.tdsSection ? <StatusPill tone="sky">TDS · {vendor.tdsSection}</StatusPill> : null}
        {vendor.paymentTerms ? <StatusPill tone="muted">{vendor.paymentTerms}</StatusPill> : null}
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-line">
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wider text-muted">We owe</span>
          {/* TODO V1.0.x — sum unpaid bills for this vendor. */}
          <span className="text-sm font-medium text-muted num">—</span>
        </div>
        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wider text-muted">YTD spend</span>
          {/* TODO V1.0.x — sum FY-bounded bills. */}
          <span className="text-sm font-medium text-muted num">—</span>
        </div>
      </div>
    </Link>
  );
}
